// ============================================================
// ThroughputEstimator — honest network throughput for LL-HLS
// ============================================================
//
// Why hls.js' own estimator is not usable for LL up-switching:
//
//   1. It samples `parsing.end - loading.start - min(actualTTFB, ewmaTTFB)`.
//      With LL blocking part requests the server holds the connection until the
//      part is produced, so most of that span is *production wait*, not transfer.
//      Only `min(actual, ewma)` of it is subtracted, so every above-average wait
//      is charged to the network and the estimate is biased down.
//   2. Sample weight is the sample's own duration (0.05-0.4 s for a part) against
//      a 9 s half-life, so the slow EWMA needs tens of seconds of flawless
//      delivery to climb, and `min(fast, slow)` always takes the pessimistic arm.
//   3. Only MAIN (video) bytes are counted while the time spent is shared with a
//      parallel audio track, and the result is compared against a BANDWIDTH that
//      covers video *and* audio.
//
// This estimator measures the only thing that matters for an up-switch decision:
// how many bytes per second the connection actually moved while it was busy.
//
//   - The clock starts at the first byte (`loading.first`), so blocking hold time
//     is excluded by construction.
//   - Concurrent transfers are handled by taking the *union* of the active
//     intervals, so audio and video downloading in parallel are not double
//     charged and their bytes add up.
//   - Full segments and probes are tracked separately: at the live edge only
//     ~35 % of the pipe is used (a 260 KB part every 417 ms), so part samples can
//     never demonstrate more than a fraction of the real capacity. A single full
//     segment saturates the link and gives an honest ceiling.

/** Which kind of request produced a sample. */
export type ThroughputSampleKind = 'part' | 'segment' | 'probe';

export interface ThroughputSample {
  /** Timestamp of the first received byte (ms, performance/Date clock). */
  startMs: number;
  /** Timestamp of the last received byte (ms, same clock as startMs). */
  endMs: number;
  /** Bytes transferred between startMs and endMs. */
  bytes: number;
  kind: ThroughputSampleKind;
}

export interface ThroughputEstimatorOptions {
  /** Samples older than this are dropped (ms). */
  windowMs?: number;
  /** Transfers smaller than this are ignored — timing noise dominates them. */
  minSampleBytes?: number;
  /**
   * Transfers shorter than this are ignored — clock granularity dominates them.
   * Kept low on purpose: `performance.now()` resolves to ~0.1 ms, while an LL
   * part is small enough to arrive in single-digit milliseconds on a fast link
   * (a 127 KB 720p part at 100 Mbit/s takes 10 ms). A higher floor rejected
   * every sample exactly when the connection was fastest, leaving the estimator
   * permanently unready and pinning auto mode to the hls.js choice.
   */
  minTransferMs?: number;
  /** Samples required before the estimate may be trusted. */
  minSamples?: number;
  /** Wall-clock span the samples must cover before the estimate may be trusted. */
  minSpanMs?: number;
  /** How long a saturating (segment/probe) sample keeps lifting the estimate. */
  burstMaxAgeMs?: number;
  /**
   * Window for the pessimistic estimate used to decide down-switches. The main
   * window is deliberately long and burst-lifted so a fast link can prove
   * itself; reading a collapse off that same number takes tens of seconds, and
   * the buffer is gone long before. Down-switches read this instead.
   */
  recentWindowMs?: number;
}

const DEFAULTS: Required<ThroughputEstimatorOptions> = {
  windowMs: 20_000,
  minSampleBytes: 8_192,
  minTransferMs: 3,
  minSamples: 6,
  minSpanMs: 3_000,
  burstMaxAgeMs: 30_000,
  recentWindowMs: 4_000,
};

/** Total length covered by a set of possibly overlapping intervals (ms). */
function unionLengthMs(samples: ThroughputSample[]): number {
  if (samples.length === 0) return 0;
  const sorted = [...samples].sort((a, b) => a.startMs - b.startMs);
  let total = 0;
  let start = sorted[0].startMs;
  let end = sorted[0].endMs;
  for (let i = 1; i < sorted.length; i++) {
    const s = sorted[i];
    if (s.startMs > end) {
      total += end - start;
      start = s.startMs;
      end = s.endMs;
    } else if (s.endMs > end) {
      end = s.endMs;
    }
  }
  return total + (end - start);
}

/** bits/s moved while at least one of these transfers was active. */
function aggregateBps(samples: ThroughputSample[]): number {
  const activeMs = unionLengthMs(samples);
  if (activeMs <= 0) return 0;
  const bytes = samples.reduce((sum, s) => sum + s.bytes, 0);
  return (8 * bytes * 1000) / activeMs;
}

export interface ThroughputSnapshot {
  bps: number;
  sustainedBps: number;
  burstBps: number;
  /** Throughput over the last `recentWindowMs`, never burst-lifted; 0 when idle. */
  recentBps: number;
  sampleCount: number;
  burstCount: number;
  spanMs: number;
  ready: boolean;
}

export class ThroughputEstimator {
  private readonly opts: Required<ThroughputEstimatorOptions>;
  private samples: ThroughputSample[] = [];

  constructor(options: ThroughputEstimatorOptions = {}) {
    this.opts = { ...DEFAULTS, ...options };
  }

  /**
   * Records a completed transfer. Returns true when the sample was kept —
   * transfers too small or too short to time reliably are discarded.
   */
  add(sample: ThroughputSample): boolean {
    const transferMs = sample.endMs - sample.startMs;
    if (
      !Number.isFinite(transferMs) ||
      transferMs < this.opts.minTransferMs ||
      !Number.isFinite(sample.bytes) ||
      sample.bytes < this.opts.minSampleBytes
    ) {
      return false;
    }
    this.samples.push(sample);
    this.prune(sample.endMs);
    return true;
  }

  /** Drops samples that fell out of the sliding window. */
  prune(nowMs: number): void {
    const cutoff = nowMs - this.opts.windowMs;
    if (this.samples.length && this.samples[0].endMs >= cutoff) return;
    this.samples = this.samples.filter(s => s.endMs >= cutoff);
  }

  reset(): void {
    this.samples = [];
  }

  snapshot(nowMs: number): ThroughputSnapshot {
    this.prune(nowMs);
    const all = this.samples;
    const bursts = all.filter(
      s => s.kind !== 'part' && nowMs - s.endMs <= this.opts.burstMaxAgeMs,
    );
    const recent = all.filter(s => nowMs - s.endMs <= this.opts.recentWindowMs);
    const sustainedBps = aggregateBps(all);
    const burstBps = aggregateBps(bursts);
    const recentBps = aggregateBps(recent);
    const spanMs = all.length
      ? all[all.length - 1].endMs - Math.min(...all.map(s => s.startMs))
      : 0;
    // A saturating sample proves capacity that part-sized transfers never can,
    // so it lifts the estimate; it can only ever raise it, never lower it.
    const bps = Math.max(sustainedBps, burstBps);
    return {
      bps,
      sustainedBps,
      burstBps,
      recentBps,
      sampleCount: all.length,
      burstCount: bursts.length,
      spanMs,
      ready:
        all.length >= this.opts.minSamples && spanMs >= this.opts.minSpanMs,
    };
  }
}
